import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  CmsPageMarket,
  MenuItem,
  MenuLinkKind,
} from '../dataProvider';
import { LINK_KIND_LABEL, MENU_LINK_KINDS, hasChildren } from './menuModel';

/**
 * The menu item authoring form (D-cms-menu). A flat CRUD form PLUS the re-parent control:
 * location (a free key), label, an OPTIONAL { kind, ref } link (kind 'none' = a non-linking
 * heading; an internal kind takes a FREE ref, 'external' takes a URL), a PARENT select (top
 * level or one of the location's top-level items, the explicit re-parent that replaces a
 * free nested-drag tree), market and the active toggle. The 2-LEVEL CAP is enforced here: an
 * item that HAS children cannot be made a child, and the parent options are only top-level
 * items. NO publish (the active toggle is the whole lifecycle). Authoring gates cms:author.
 */

const TOP_LEVEL = '__top__';
const NO_LINK = 'none';

const MenuItemFormInner = ({ record }: { record?: MenuItem }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  const [location, setLocation] = useState(record?.location ?? 'header');
  const [label, setLabel] = useState(record?.label ?? '');
  const [linkKind, setLinkKind] = useState<MenuLinkKind | typeof NO_LINK>(
    record?.link?.kind ?? NO_LINK,
  );
  const [linkRef, setLinkRef] = useState(record?.link?.ref ?? '');
  const [parentId, setParentId] = useState<string>(
    record?.parentId != null ? String(record.parentId) : TOP_LEVEL,
  );
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [active, setActive] = useState(record?.active ?? true);

  // The location's items, to populate the parent options (top-level items only).
  const { data: locationItems } = useGetList<MenuItem>('menuItems', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'order', order: 'ASC' },
    filter: { location },
  });
  const all = locationItems ?? [];

  // The 2-LEVEL CAP: an item that HAS children cannot itself be made a child.
  const itemHasChildren = record != null && hasChildren(all, record.id);
  // Parent options: the location's top-level items, excluding self (cannot parent under a
  // child, and cannot parent under self). With the cap, only top-level items are valid.
  const parentOptions = all.filter(
    (i) => i.parentId === null && i.id !== record?.id,
  );

  const validate = (): string | null => {
    if (!location.trim()) return 'A location is required';
    if (!label.trim()) return 'A label is required';
    if (linkKind !== NO_LINK && !linkRef.trim()) {
      return 'A link reference is required for a linked item';
    }
    return null;
  };

  // The next order at the END of a level (a parent's children, or the top level).
  const nextOrder = (place: string, parent: number | null): number => {
    const siblings = all.filter(
      (i) => i.location === place && i.parentId === parent,
    );
    return siblings.reduce((max, s) => Math.max(max, s.order + 1), 0);
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    // Resolve the parent. The cap forces an item with children to stay top level.
    const resolvedParent =
      itemHasChildren || parentId === TOP_LEVEL ? null : Number(parentId);
    const link =
      linkKind === NO_LINK ? undefined : { kind: linkKind, ref: linkRef };
    const nowIso = new Date().toISOString();
    try {
      if (isEdit && record) {
        // If the parent changed, the item moves to the END of the new level.
        const parentChanged = resolvedParent !== record.parentId;
        const order = parentChanged
          ? nextOrder(location, resolvedParent)
          : record.order;
        await dataProvider.update('menuItems', {
          id: record.id,
          data: {
            location,
            parentId: resolvedParent,
            order,
            label,
            link,
            market,
            active,
            updatedAt: nowIso,
          },
          previousData: record,
        });
        notify('Menu item saved', { type: 'success' });
        redirect('list', 'menuItems');
      } else {
        await dataProvider.create('menuItems', {
          data: {
            location,
            parentId: resolvedParent,
            order: nextOrder(location, resolvedParent),
            label,
            link,
            market,
            active,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Menu item created', { type: 'success' });
        redirect('list', 'menuItems');
      }
    } catch {
      notify('Could not save the menu item', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit menu item' : 'New menu item'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              helperText="A menu location, e.g. header or footer"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'menu-location' }}
            />
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'menu-market-input' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'menu-label' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Link kind"
              value={linkKind}
              onChange={(e) =>
                setLinkKind(e.target.value as MenuLinkKind | typeof NO_LINK)
              }
              margin="dense"
              size="small"
              sx={{ minWidth: 200 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'menu-link-kind' }}
            >
              <option value={NO_LINK}>None (heading)</option>
              {MENU_LINK_KINDS.map((k) => (
                <option key={k} value={k}>
                  {LINK_KIND_LABEL[k]}
                </option>
              ))}
            </TextField>
            {linkKind !== NO_LINK && (
              <TextField
                label="Link reference"
                value={linkRef}
                onChange={(e) => setLinkRef(e.target.value)}
                helperText={
                  linkKind === 'external'
                    ? 'A URL'
                    : 'A free path or slug (e.g. about, beauty)'
                }
                margin="dense"
                size="small"
                fullWidth
                inputProps={{ 'data-testid': 'menu-link-ref' }}
              />
            )}
          </Stack>
          <TextField
            select
            label="Parent"
            value={itemHasChildren ? TOP_LEVEL : parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={itemHasChildren}
            helperText={
              itemHasChildren
                ? 'This item has children, so it must stay top level (the 2 level cap).'
                : 'Top level, or nest under a top-level item.'
            }
            margin="dense"
            size="small"
            fullWidth
            slotProps={{ select: { native: true } }}
            inputProps={{ 'data-testid': 'menu-parent' }}
          >
            <option value={TOP_LEVEL}>Top level</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.label}
              </option>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                data-testid="menu-active-switch"
              />
            }
            label="Active"
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="menu-save">
          {isEdit ? 'Save' : 'Create item'}
        </Button>
        <Button onClick={() => redirect('list', 'menuItems')}>Cancel</Button>
      </Stack>
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const MenuItemCreateView = () => <MenuItemFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const MenuItemEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<MenuItem>('menuItems', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <MenuItemFormInner record={record} />;
};

export const MenuItemCreate = () => (
  <RequireAccess capability="cms:author">
    <MenuItemCreateView />
  </RequireAccess>
);

export const MenuItemEdit = () => (
  <RequireAccess capability="cms:author">
    <MenuItemEditView />
  </RequireAccess>
);
